import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  fetchCustomDashboards,
  fetchFacilitiesHierarchy,
} from '../../../redux/slices/CustomDashboardSlice';
import { fetchListChargingPointFilters } from '../../../redux/slices/DeviceManagementSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import dashboard from '../../../utils/api/dashboard';

import ChargingScheduleChart from './components/ChargingScheduleChart';
import ConnectorStatusChart from './components/ConnectorStatusChart';
import DashboardCards from './components/DashboardCards';
import DashboardMap from './components/DashboardMap';
import DashboardToolbar from './components/DashboardToolbar';
// import DeviceDurationChart from './components/DeviceDurationChart';
import DeviceStatus from './components/DeviceStatus';
import DeviceUtilizationChart from './components/DeviceUtilization';
// import LiveAlerts from './components/LiveAlerts';
import NewStationPerformanceChart from './components/NewStationPerformanceChart';
import PenaltyRevenueChart from './components/PenaltyRevenueChart';
import RevenuePowerConsumedChart from './components/RevenuePowerConsumedChart';
// import StationAlertsChart from './components/StationAlertsChart';
import WorkspaceView from './components/WorkspaceView';

/**
 * Dashboard component
 */
function Dashboard() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { filters, filterListLoading } = useAppSelector((s) => s.device);
  const { selectedDashboardId, dashboards } = useAppSelector((state) => state.customDashboard);
  const selectedDashboard = dashboards.find((d) => d.id === selectedDashboardId);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: dayjs().subtract(6, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  });
  const [dashboardValue, setDashboardValue] = useState<any>();
  const [loading, setLoading] = useState<boolean>(true);
  const [appliedFilters, setAppliedFilters] = useState<{
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  }>({
    facilityIds: [],
    stationIds: [],
    deviceIds: [],
    type: ['commercial'],
  });

  /**
   * Handles date range picker change
   * @param {string} startDate - The selected range start date
   * @param {string} endDate - The selected range end date
   */
  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setDateRange({ startDate, endDate });
  };

  /**
   * Handles filter application from DashboardFilter
   * @param {any} selectedFilters - Selected filter criteria
   * @param {any} selectedFilters.facilityIds - The facilityIds prop
   * @param {any} selectedFilters.stationIds - The stationIds prop
   * @param {any} selectedFilters.deviceIds - The deviceIds prop
   * @param {any} selectedFilters.type - The type prop
   */
  const handleApplyFilters = (selectedFilters: {
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  }) => {
    setAppliedFilters(selectedFilters);
  };

  useEffect(() => {
    setLoading(true);
    dashboard
      .getDefaultCustomDashboard()
      .then((response: any) => {
        setDashboardValue(response?.data);
        setLoading(false);
      })
      .catch((error: any) => {
        console.error('error', error);
        setLoading(false);
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || 'Failed to fetch default dashboard',
            type: 'error',
          }),
        );
      });
    const filterWithOutSearchBy = filters.filter((eachFilter:any) => eachFilter.key !== 'searchBy');
    if (filterWithOutSearchBy.length === 0 && !filterListLoading) {
      dispatch(fetchListChargingPointFilters());
    }
    dispatch(setPageTitle(''));
    dispatch(fetchCustomDashboards({}));
    dispatch(fetchFacilitiesHierarchy());
  }, []);
  if (loading) {
    return (
      <div className="dashboard-wrap-loader">
        <BatteryLoader />
      </div>
    );
  }

  return (
    <div className="dashboard-wrap">
      <DashboardToolbar
        onCustomize={() => navigate('/ev-admin/dashboard/customize-dashboard')}
        startDate={dateRange.startDate}
        endDate={dateRange.endDate}
        onDateRangeChange={handleDateRangeChange}
        onApply={handleApplyFilters}
        onClear={() =>
          setAppliedFilters({
            facilityIds: [],
            stationIds: [],
            deviceIds: [],
            type: ['commercial'],
          })
        }
        filters={appliedFilters}
      />
      {(!dashboardValue || Object.keys(dashboardValue).length === 0) &&
      selectedDashboardId === 'default' ? (
        <>
          <DashboardCards
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            filters={appliedFilters}
          />
          <div className="dashboard-content">
            <div className="map-bar-chart">
              <DashboardMap filters={appliedFilters} />
              <DeviceStatus filters={appliedFilters} />
              {/* <DeviceDurationChart /> */}
              {/* <LiveAlerts filters={appliedFilters} /> */}
            </div>
            <div className="performance-connector-device-status">
              <NewStationPerformanceChart filters={appliedFilters} />
              <ConnectorStatusChart filters={appliedFilters} />
            </div>
            {/* <StationOverview /> */}
            <div className="revenue-station-alert-wrap">
              <RevenuePowerConsumedChart
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                filters={appliedFilters}
              />
              <PenaltyRevenueChart
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                filters={appliedFilters}
              />
              {/* <StationAlertsChart startDate={dateRange.startDate} endDate={dateRange.endDate} filters={appliedFilters} /> */}
              {/* <ChargerStatusChart /> */}
            </div>
            <div className="device-related-chart">
              <ChargingScheduleChart
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                filters={appliedFilters}
              />
              <DeviceUtilizationChart
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                filters={appliedFilters}
              />
            </div>
            {/* <div className="utilization-chart">
              <DeviceUtilizationChart startDate={dateRange.startDate} endDate={dateRange.endDate} filters={appliedFilters} />
            </div> */}
          </div>
        </>
      ) : (
        <WorkspaceView
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          filters={appliedFilters}
          selectedDashboard={selectedDashboardId === 'default' ? dashboardValue : selectedDashboard}
        />
      )}
    </div>
  );
}

export default Dashboard;
